import { LogErrors } from './../../LogErrors'

import { GetAgendas as BLGetAgendas, GetCourses as BLGetCourses, GetEvaluations as BLGetEvaluations, CreateAgenda as DLCreateAgenda, RescheduleAgenda as DLRescheduleAgenda, DeleteAgenda as DLDeleteAgenda, GetExistingAgenda as DLGetExistingAgenda, GetCourseProgressSummary as DLGetCourseProgressSummary } from './../../DL/Instructor/Agendas'

//--- Comunes ---//

const ConvertToInputDate = (dateString) => {
    try {
        if (!dateString || typeof dateString !== 'string') return ''

        // Si ya está en formato YYYY-MM-DD, no lo conviertas
        const isoFormatRegex = /^\d{4}-\d{2}-\d{2}$/
        if (isoFormatRegex.test(dateString)) return dateString

        // Si está en formato DD-MM-YYYY, lo convertimos
        const [day, month, year] = dateString.split('-')
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    } catch (error) {
        console.error('Error en ConvertToInputDate:', error)
        return ''
    }
}

const ConvertToBackendDate = (inputDate) => {
    try {
        if (!inputDate || !inputDate.includes('-')) return ''
        const [year, month, day] = inputDate.split('-')
        return `${day}-${month}-${year}` // DD-MM-YYYY    
    } catch (error) {
        
    }
}

//--- Comunes ---//

// --- Helpers --- //

// --- Helpers de fecha/hora --- //
const pad2 = (n) => String(n).padStart(2, '0')

const FormatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return ''
  const iso = /^\d{4}-\d{2}-\d{2}$/   // YYYY-MM-DD
  const dmy = /^\d{2}-\d{2}-\d{4}$/   // DD-MM-YYYY

  if (iso.test(dateStr)) {
    const [y, m, d] = dateStr.split('-')
    return `${pad2(d)}-${pad2(m)}-${y}`
  }
  if (dmy.test(dateStr)) return dateStr

  // Fallback muy tolerante (por si viniera otro formato legible por Date)
  try {
    const dt = new Date(dateStr)
    if (!isNaN(dt)) {
      return `${pad2(dt.getDate())}-${pad2(dt.getMonth() + 1)}-${dt.getFullYear()}`
    }
  } catch {}
  return ''
}

const NormalizeTime = (timeStr) => {
  if (!timeStr) return ''
  const parts = timeStr.split(':')
  if (parts.length === 2) return `${pad2(parts[0])}:${pad2(parts[1])}:00`
  if (parts.length === 3) return `${pad2(parts[0])}:${pad2(parts[1])}:${pad2(parts[2])}`
  return ''
}

const BuildRangeText = (startDate, startHour, finishDate, finishHour) => {
  const d1 = FormatDateDDMMYYYY(startDate)
  const d2 = FormatDateDDMMYYYY(finishDate)
  const t1 = NormalizeTime(startHour)
  const t2 = NormalizeTime(finishHour)

  return `Del ${d1} al ${d2} entre las ${t1} hrs. y ${t2} hrs.`
}

// --- Uso con tu data --- //
const EnrichWithDate = async (rows) => {
    if (!Array.isArray(rows)) return []

    return rows.map(r => ({
        ...r,
        name: r.groupName,
        date: BuildRangeText(r.startDate, r.startHour, r.finishDate, r.finishHour)
    }))
}

const GetAgendas = async () => {
    try {
        // Validar que exista la info del usuario en el session storage
        if (!sessionStorage.getItem('userData')) {
            return { success: false, message: '' } //En este caso borraron los datos; así que le muestro mensaje indicando que su sesión ha expirado.
        }
    
        // Obtener la info del usuario desde el session storage
        const userData = JSON.parse(sessionStorage.getItem('userData'))
    
        const token = userData?.token
        if (!token) {
            return { success: false, message: '' }
        }
        
        const idCenter = userData?.navigation?.center?.id
        if (!idCenter) {
            return { success: false, message: '' }
        }
        
        const response = await BLGetAgendas(idCenter, token)
        
        if (response.success) {
            // Agregar a cada registro la propiedad date
            const responseDate = await EnrichWithDate(response.data)
            
            if (responseDate) {
                return { success: true, data: responseDate }
            } else {
                return { success: false, message: '' }
            }
        } else {
            return { success: false, message: 'No ha sido posible obtener el listado de agendas. Inténtalo nuevamente o inténtalo más tarde.' }
        }
    } catch (error) {
        await LogErrors('GetAgendas - BL Agendas', error)
        return { success: false, message: 'No ha sido posible obtener las evaluaciones agendadas. Intenta actualizar la página o intenta actualizarla más tarde.' }
    }
}

const ScheduleAgenda = async (agenda, rows) => {
  try {
    if (!agenda || !agenda.name || !agenda.group || !agenda.type || !agenda.startDate || !agenda.startHour || !agenda.endDate || !agenda.endHour) {
      return { success: false, message: 'Faltan campos obligatorios para agendar la evaluación.' }
    }

    const startTime = NormalizeTime(agenda.startHour)
    const endTime   = NormalizeTime(agenda.endHour)

    const newId = Math.max(0, ...rows.map(row => row.id)) + 1

    const newAgenda = {
      id: newId,
      name: agenda.name,
      group: agenda.group,
      type: agenda.type,
      startDate: agenda.startDate,
      startHour: startTime,
      endDate: agenda.endDate,
      endHour: endTime,

      // Aliases para mantener compatibilidad con otros puntos del código:
      finishDate: agenda.endDate,
      finishHour: endTime,

      // Texto en el nuevo formato:
      date: BuildRangeText(agenda.startDate, startTime, agenda.endDate, endTime)
    }

    const updatedRows = [...rows, newAgenda]
    return { success: true, data: updatedRows }
  } catch (error) {
    console.error('Error en ScheduleAgenda:', error)
    return {
      success: false,
      message: 'No ha sido posible agendar la evaluación. Inténtalo nuevamente o inténtalo más tarde.'
    }
  }
}

const RescheduleAgenda = async (payload, rows = []) => {
  try {
    const pad2 = (n) => String(n).padStart(2, '0')

    const toHHMMSS = (t) => {
      if (!t) return ''
      const p = t.split(':')
      if (p.length === 2) return `${pad2(p[0])}:${pad2(p[1])}:00`
      if (p.length === 3) return `${pad2(p[0])}:${pad2(p[1])}:${pad2(p[2])}`
      return ''
    }

    // NUEVO: usa el mismo formato de arriba (solo fechas, y horas separadas)
    const buildRangeText = (startDate, startHour, finishDate, finishHour) => {
      const d1 = FormatDateDDMMYYYY(startDate)
      const d2 = FormatDateDDMMYYYY(finishDate)
      const t1 = NormalizeTime(startHour)
      const t2 = NormalizeTime(finishHour)
      return `Del ${d1} al ${d2} entre las ${t1} hrs. y ${t2} hrs.`
    }

    const hasValue = (v) => v !== undefined && v !== null && v !== ''

    const req = ['idAgenda', 'startDate', 'finishDate', 'startHour', 'finishHour']
    for (const k of req) {
      if (!hasValue(payload?.[k])) {
        return { success: false, message: `Falta ${k}` }
      }
    }

    const normalized = {
      ...payload,
      startHour: toHHMMSS(payload.startHour),
      finishHour: toHHMMSS(payload.finishHour)
    }

    const rawUser = sessionStorage.getItem('userData')
    if (!rawUser) return { success: false, message: 'Tu sesión ha expirado. Ingresa nuevamente.' }
    const token = JSON.parse(rawUser)?.token
    if (!token) return { success: false, message: 'Tu sesión ha expirado. Ingresa nuevamente.' }

    const dlPayload = {
      idAgenda: normalized.idAgenda,
      startDate: normalized.startDate,
      finishDate: normalized.finishDate,
      startHour: normalized.startHour,
      finishHour: normalized.finishHour,
      currentOpportunities: payload.currentOpportunities
    }

    const dlResp = await DLRescheduleAgenda(dlPayload, token)
    
    if (!dlResp?.success) {
      return { success: false, message: dlResp?.message ?? 'No ha sido posible reagendar la evaluación.' }
    }

    const idPayload = normalized.idAgenda
    
    const matchByAnyId = (r) => {
      const rowIds = [r.id, r.Id, r.idAgenda, r.IdAgenda, r.id_agenda]
      return rowIds.some((rid) => rid === idPayload)
    }

    let found = false
    
    const updatedRows = rows.map((r) => {
      if (!matchByAnyId(r)) return r
      found = true

      const startDate  = normalized.startDate
      const startHour  = normalized.startHour
      const finishDate = normalized.finishDate
      const finishHour = normalized.finishHour

      return {
        ...r,
        startDate,
        startHour,
        finishDate,
        finishHour,
        // Mantén el mismo formato que en Schedule/Enrich:
        date: buildRangeText(startDate, startHour, finishDate, finishHour),
        currentOpportunities: payload.currentOpportunities
      }
    })

    if (!found) {
      console.warn('No se encontró el row a actualizar.', { IdAgenda: idPayload })
    }

    return { success: true, data: updatedRows }
  } catch (err) {
    console.error('RescheduleAgenda - BL Agendas:', err)
    return { success: false, message: 'Error inesperado al reagendar. Inténtalo más tarde.' }
  }
}

const DeleteAgenda = async (idAgenda, idGroup, idCourse, idItem, rows = []) => {
    try {
        if (!idAgenda || !idGroup || !idCourse || !idItem) {
            return { success: false, message: 'No ha sido posible eliminar la evaluación agendada. Inténtalo nuevamente o inténtalo más tarde.' }
        }
        // Validar que exista la info del usuario en el session storage
        if (!sessionStorage.getItem('userData')) {
            return { success: false, message: '' } //En este caso borraron los datos; así que le muestro mensaje indicando que su sesión ha expirado.
        }
    
        // Obtener la info del usuario desde el session storage
        const userData = JSON.parse(sessionStorage.getItem('userData'))
    
        const token = userData?.token
        if (!token) {
            return { success: false, message: '' }
        }
        
        const idCenter = userData?.navigation?.center?.id
        if (!idCenter) {
            return { success: false, message: '' }
        }
        
        const response = await DLDeleteAgenda(idAgenda, idCenter, idGroup, idCourse, idItem, token)
        
        if (response.success) {
            const updatedRows = rows.filter(z => z.id !== idAgenda)
    
            return { success: true, data: updatedRows }    
        } else {
            return { success: false, message: response.message }
        }
    } catch (error) {
        console.error('Error en DeleteAgenda:', error)
        return { success: false, message: 'No ha sido posible eliminar la evaluación agendada. Inténtalo nuevamente o inténtalo más tarde.' }
    }
}

//--- Agenda Create ---//

const GetCourses = async () => {
    try {
        // Validar que exista la info del usuario en el session storage
        if (!sessionStorage.getItem('userData')) {
            return { success: false, message: '' } //En este caso borraron los datos; así que le muestro mensaje indicando que su sesión ha expirado.
        }
    
        // Obtener la info del usuario desde el session storage
        const userData = JSON.parse(sessionStorage.getItem('userData'))
    
        const token = userData?.token
        if (!token) {
            return { success: false, message: '' }
        }
        
        const idCenter = userData?.navigation?.center?.id
        if (!idCenter) {
            return { success: false, message: '' }
        }

        // const idGroup = group?.idGroup
        // if (!idGroup) {
        //     return { success: false, message: '' }
        // }
        
        //const response = await BLGetCourses(idCenter, idGroup, token)
        const response = await BLGetCourses(idCenter, token) //idGroup
        
        if (response.success) {
            return { success: true, data: response.data }
        } else {
            return { success: false, message: '' }
        }
    } catch (error) {
        console.error('Error en GetCourses:', error)
        return { success: false, message: 'No ha sido posible obtener los cursos. Inténtalo nuevamente o inténtalo más tarde.' }
    }
}

const GetEvaluations = async (idCourse) => {
    try {
        // Validar que exista la info del usuario en el session storage
        if (!sessionStorage.getItem('userData')) {
            return { success: false, message: '' } //En este caso borraron los datos; así que le muestro mensaje indicando que su sesión ha expirado.
        }
    
        // Obtener la info del usuario desde el session storage
        const userData = JSON.parse(sessionStorage.getItem('userData'))
    
        const token = userData?.token
        if (!token) {
            return { success: false, message: '' }
        }
        
        const idCenter = userData?.navigation?.center?.id
        if (!idCenter) {
            return { success: false, message: '' }
        }

        if (!idCourse) {
            return { success: false, message: '' }
        }
        
        const response = await BLGetEvaluations(idCenter, idCourse, token)
        
        if (response.success) {
            return { success: true, data: response.data }
        } else {
            return { success: false, message: '' }
        }
    } catch (error) {
        console.error('Error en GetEvaluations:', error)
        return { success: false, message: 'No ha sido posible obtener las evaluaciones. Inténtalo nuevamente o inténtalo más tarde.' }
    }
}




// === Configurable ===
const MAX_NAME_LEN = 100
const MIN_OPS = 1
const MAX_OPS = 10 // ajusta según tu regla de negocio

// === Helpers ===
//const pad2 = (n) => String(n).padStart(2, '0')

const todayISO = () => {
  const now = new Date()

  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
}

const isPosInt = (v) => Number.isInteger(v) && v > 0

const toHHMMSS = (t) => {
  if (!t) return ''

  const parts = t.split(':')

  if (parts.length === 2) return `${pad2(parts[0])}:${pad2(parts[1])}:00`
  if (parts.length === 3) return `${pad2(parts[0])}:${pad2(parts[1])}:${pad2(parts[2])}`

  return ''
}

// Fecha ISO básica + chequeo de calendario (incluye bisiesto)
const isValidISODate = (dateStr) => {
  if (typeof dateStr !== 'string') return false

  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return false

  const y = +m[1], mo = +m[2], d = +m[3]
  
  if (mo < 1 || mo > 12) return false
  
  const dim = [31, (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mo - 1]

  return d >= 1 && d <= dim
}

// const isValidTimeHHMMSS = (t) => {
//   if (typeof t !== 'string') return false

//   const m = t.match(/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/)
//   return !!m
// }

const isValidTimeHHMMSS = (t) => {
  if (typeof t !== 'string') return false

  return /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(t)
}

const toDateObj = (d, t) => {
  const [y, m, day] = d.split('-').map(Number)
  const [h, mi, s] = t.split(':').map(Number)

  return new Date(y, m - 1, day, h, mi, s, 0) // Local time
}

const diffMinutes = (d1, t1, d2, t2) => {
  const a = toDateObj(d1, t1).getTime()
  const b = toDateObj(d2, t2).getTime()

  return Math.round((b - a) / 60000) // entero en minutos
}

// Nombre opcional: si viene, se valida. Si no viene, no es error.
const isValidOptionalName = (s) => {
  if (s == null || s === '') return true
  
  const name = String(s).trim()
  if (name.length < 1 || name.length > MAX_NAME_LEN) return false
  
  return /^[\p{L}\p{N}\s._-]+$/u.test(name)
}

// Compara (StartDate, StartHour) vs (FinishDate, FinishHour) sin zonas horarias
const cmpDateTime = (d1, t1, d2, t2) => {
  const [y1, m1, day1] = d1.split('-').map(Number)
  const [h1, mi1, s1] = t1.split(':').map(Number)
  const [y2, m2, day2] = d2.split('-').map(Number)
  const [h2, mi2, s2] = t2.split(':').map(Number)

  // Comparación lexicográfica por partes
  if (y1 !== y2) return y1 < y2 ? -1 : 1
  if (m1 !== m2) return m1 < m2 ? -1 : 1
  if (day1 !== day2) return day1 < day2 ? -1 : 1
  if (h1 !== h2) return h1 < h2 ? -1 : 1
  if (mi1 !== mi2) return mi1 < mi2 ? -1 : 1
  if (s1 !== s2) return s1 < s2 ? -1 : 1

  return 0
}

// // Nombre: no vacío, longitud y caracteres permitidos (letras, números, espacios, . _ -)
// const isValidName = (s) => {
//   if (typeof s !== 'string') return false

//   const name = s.trim()
//   if (name.length < 1 || name.length > MAX_NAME_LEN) return false
  
//   // Permite acentos y letras unicode
//   return /^[\p{L}\p{N}\s._-]+$/u.test(name)
// }

// // === Validador principal ===
// const ValidateAgendaPayload = async (input) => {
//   const errors = []
//   const warnings = []
//   const normalized = { ...input }

//   // ---- Name ----
//   if (!isValidName(normalized.name)) {
//     errors.push(`Name inválido: obligatorio, 1–${MAX_NAME_LEN} caracteres, sin símbolos extraños.`)
//   } else {
//     normalized.name = normalized.name.trim()
//   }

//   // ---- Fechas ----
//   if (!isValidISODate(normalized.startDate)) {
//     errors.push('StartDate inválido. Formato requerido: YYYY-MM-DD y fecha real.')
//   }
//   if (!isValidISODate(normalized.finishDate)) {
//     errors.push('FinishDate inválido. Formato requerido: YYYY-MM-DD y fecha real.')
//   }

//   // ---- Horas ----
//   // Acepta HH:mm o HH:mm:ss normaliza a HH:mm:ss
//   const startHourNorm = toHHMMSS(normalized.startHour)
//   const finishHourNorm = toHHMMSS(normalized.finishHour)

//   if (!startHourNorm || !isValidTimeHHMMSS(startHourNorm)) {
//     errors.push('StartHour inválido. Usa HH:mm o HH:mm:ss (00–23:59:59).')
//   }
//   if (!finishHourNorm || !isValidTimeHHMMSS(finishHourNorm)) {
//     errors.push('FinishHour inválido. Usa HH:mm o HH:mm:ss (00–23:59:59).')
//   }

//   // Si horas válidas, actualiza normalizado
//   if (!errors.length) {
//     normalized.startHour = startHourNorm
//     normalized.finishHour = finishHourNorm

//     // Consistencia temporal: inicio < fin (estricto)
//     const cmp = cmpDateTime(normalized.startDate, normalized.startHour, normalized.finishDate, normalized.finishHour)
//     if (cmp >= 0) {
//       errors.push('La fecha/hora de inicio debe ser estrictamente anterior a la fecha/hora de fin.')
//     }
//   }

//   // ---- Oportunities ----
//   if (!Number.isInteger(normalized.oportunities)) {
//     errors.push('Oportunities debe ser un entero.')
//   } else if (normalized.oportunities < MIN_OPS || normalized.oportunities > MAX_OPS) {
//     errors.push(`Oportunities debe estar entre ${MIN_OPS} y ${MAX_OPS}.`)
//   }

//   // ---- IDs ----
//   if (!isPosInt(normalized.idItem))  errors.push('IdItem debe ser entero positivo.')
//   if (!isPosInt(normalized.idGroup)) errors.push('IdGroup debe ser entero positivo.')
//   if (!isPosInt(normalized.idCourse)) errors.push('IdCourse debe ser entero positivo.')

//   return {
//     ok: errors.length === 0,
//     errors,
//     warnings,
//     normalized
//   }
// }

// === Validador individual (con fieldErrors) ===
const ValidateAgendaPayload = async (agenda) => {
  try {
    const errors = []
    const fieldErrors = {} // { startDate:true, finishDate:true, startHour:true, finishHour:true, opportunities:true, name:true }
    const normalized = { ...agenda }

    // --- Autoridad: si viene `id`, úsalo; si no, cae a `idItem`.
    //normalized.idItem = Number.isInteger(agenda?.id)
    normalized.idItem = Number(agenda?.idItem)

    // ---- Name (opcional) ----
    if (!isValidOptionalName(normalized.name)) {
      errors.push(`Nombre inválido: 1–${MAX_NAME_LEN} caracteres, sin símbolos extraños.`)
      fieldErrors.name = true
    } else if (normalized.name) {
      normalized.name = normalized.name.trim()
    }

    // ---- Fechas ----
    if (!isValidISODate(normalized.startDate)) {
      errors.push('Fecha inicio inválida. Formato YYYY-MM-DD y fecha real.')
      fieldErrors.startDate = true
    }
    if (!isValidISODate(normalized.finishDate)) {
      errors.push('Fecha fin inválida. Formato YYYY-MM-DD y fecha real.')
      fieldErrors.finishDate = true
    }

    // Si la fecha inicio es válida, debe ser ≥ hoy (solo si NO es existente)
    if (isValidISODate(normalized.startDate)) {
      const today = todayISO()
      //if (normalized.startDate < today) {
      //if (!agenda?.isExistingAgenda && normalized.startDate < today) {
      if (!agenda?.isExist && normalized.startDate < today) {
        errors.push('La fecha de inicio no puede ser anterior a hoy.')
        fieldErrors.startDate = true
      }
    }

    // ---- Horas ---- (acepta HH:mm o HH:mm:ss normaliza a HH:mm:ss)
    const startHourNorm = toHHMMSS(normalized.startHour)
    const finishHourNorm = toHHMMSS(normalized.finishHour)

    if (!startHourNorm || !isValidTimeHHMMSS(startHourNorm)) {
      errors.push('Hora de inicio inválida. Usa HH:mm o HH:mm:ss (00–23:59:59).')
      fieldErrors.startHour = true
    }
    if (!finishHourNorm || !isValidTimeHHMMSS(finishHourNorm)) {
      errors.push('Hora de fin inválida. Usa HH:mm o HH:mm:ss (00–23:59:59).')
      fieldErrors.finishHour = true
    }

    if (!errors.length) {
      // Fechas/horas pasaron formatos → normaliza
      normalized.startHour = startHourNorm
      normalized.finishHour = finishHourNorm

      // Fin debe ser al menos +1 minuto respecto a inicio
      const minutes = diffMinutes(normalized.startDate, normalized.startHour, normalized.finishDate, normalized.finishHour)
      if (minutes < 1) {
        errors.push('La fecha/hora de fin debe ser al menos 1 minuto mayor que la de inicio.')
        // resalta ambos, si prefieres solo finishHour/finishDate, quita start*
        fieldErrors.startDate = true
        fieldErrors.startHour = true
        fieldErrors.finishDate = true
        fieldErrors.finishHour = true
      }
    }

    // ---- Oportunidades ----
    if (!Number.isInteger(normalized.oportunities)) {
      errors.push('Oportunidades debe ser un entero.')
      fieldErrors.opportunities = true
    } else if (normalized.oportunities < MIN_OPS || normalized.oportunities > MAX_OPS) {
      errors.push(`Oportunidades debe estar entre ${MIN_OPS} y ${MAX_OPS}.`)
      fieldErrors.opportunities = true
    }

    // ---- IDs ----
    if (!isPosInt(normalized.idItem))  errors.push('Elemento inválido.') //IdItem inválido
    if (!isPosInt(normalized.idGroup)) errors.push('Grupo inválido.') //IdGroup inválido.
    if (!isPosInt(normalized.idCourse)) errors.push('Curso inválido.') //IdCourse inválido.

    return { success: true,
      data: {
        errors: errors,
        fieldErrors: fieldErrors,
        normalized: normalized
      }
    }

    // return {
    //   ok: errors.length === 0,
    //   errors,
    //   fieldErrors,
    //   normalized
    // }
  } catch (error) {
    return { success: false, message: error }
  }
}

// === Validador por lote para la UI (resumen + mapa de errores por ID) ===
const ValidateAgendasBatch = async (agendas = []) => {
  try {
    const summary = {
      valid: [],
      invalid: [],
      fieldErrorsById: {}
    }

    // const valid = []
    // const invalid = []
    // const fieldErrorsById = {} // { [idItem]: {startDate:true,...} }

    for (const agenda of agendas) {
      const response = await ValidateAgendaPayload(agenda)
      //const { ok, errors, fieldErrors, normalized } = await ValidateAgendaPayload(agenda)

      if (response.success) {
        summary.valid.push(response.data.normalized)
      } else {
        summary.invalid.push({ idItem: agenda.idItem, errors: response.data.errors })
        summary.fieldErrorsById[agenda.idItem] = response.data.fieldErrors || {}
      }
    }

    //const summaryText = `Evaluaciones agendadas: ${valid.length}. Evaluaciones que no se pudieron agendar: ${invalid.length}.`
    //return { valid, invalid, fieldErrorsById } //summaryText
    return { success: true, data: summary }
  } catch (error) {
    return { success: true, message: '' }
  }
}

const CreateAgenda = async (agenda) => {
    try {
        // Validar que exista la info del usuario en el session storage
        if (!sessionStorage.getItem('userData')) {
            return { success: false, message: '' } //En este caso borraron los datos; así que le muestro mensaje indicando que su sesión ha expirado.
        }
    
        // Obtener la info del usuario desde el session storage
        const userData = JSON.parse(sessionStorage.getItem('userData'))
    
        const token = userData?.token
        if (!token) {
            return { success: false, message: '' }
        }
        
        if (!agenda) {
            return { success: false, message: '' }
        }
        
        //const { ok, errors, normalized } = await ValidateAgendaPayload(agenda)
        const responseValidate = await ValidateAgendaPayload(agenda)
        
        if (responseValidate.success) {
          // Enviar normalized al endpoint
          const response = await DLCreateAgenda(responseValidate.data.normalized, token)
          
          if (response.success) {
              return { success: true, data: response.data }
          } else {
              return { success: false, message: response.message }
          }
        } else {
          // Mostrar errores en UI
          console.log(responseValidate.data.errors)
        }
    } catch (error) {
        console.log('Error en CreateAgenda:', error)
        return { success: false, message: 'No ha sido posible crear la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }
}

const GetExistingAgenda = async (group, course, item) => {
  try {
    // Validar que exista la info del usuario en el session storage
    if (!sessionStorage.getItem('userData')) {
        return { success: false, message: '' } //En este caso borraron los datos; así que le muestro mensaje indicando que su sesión ha expirado.
    }

    // Obtener la info del usuario desde el session storage
    const userData = JSON.parse(sessionStorage.getItem('userData'))

    const token = userData?.token
    if (!token) {
        return { success: false, message: '' }
    }
    
    if (!group || isNaN(group)) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }

    if (!course || isNaN(course)) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }

    if (!item || isNaN(item)) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }
    
    const response = await DLGetExistingAgenda(group, course, item, token)
    
    if (response.success) {
      return { success: true, data: response.data }
    } else {
      return { success: false, message: response.message }
    }
  } catch (error) {
    console.error('Error en GetExistingAgenda:', error)
    return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
  }
}

//--- Agenda Create ---//

//--- Progress Instructor ---//

const GetCourseProgressSummary = async (group, course, userIds) => {
  try {
    // Validar que exista la info del usuario en el session storage
    if (!sessionStorage.getItem('userData')) {
        return { success: false, message: '' } //En este caso borraron los datos; así que le muestro mensaje indicando que su sesión ha expirado.
    }

    // Obtener la info del usuario desde el session storage
    const userData = JSON.parse(sessionStorage.getItem('userData'))

    const token = userData?.token
    if (!token) {
        return { success: false, message: '' }
    }
    
    const center = userData?.navigation?.center?.id
    if (!center) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }

    if (!group || isNaN(group)) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }

    if (!course || isNaN(course)) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }

    if (!userIds) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }
    
    if (!Array.isArray(userIds)) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }

    if (userIds.length === 0) {
      return { success: false, message: 'No ha sido posible obtener la información de la agenda deseada. Inténtalo nuevamente o inténtalo más tarde.' }
    }
    
    const response = await DLGetCourseProgressSummary(center, course, group, 2, userIds, token)
    
    if (response.success) {
      return { success: true, data: response.data }
    } else {
      return { success: false, message: response.message }
    }
  } catch (error) {
    console.error('Error en BL GetCourseProgressSummary:', error)
    return { success: false, message: 'No ha sido posible obtener la información deseada. Inténtalo nuevamente o inténtalo más tarde.' }
  }
}

//--- Progress Instructor ---//

export { GetAgendas, DeleteAgenda, ScheduleAgenda, RescheduleAgenda, ConvertToInputDate, GetCourses, GetEvaluations, CreateAgenda, ValidateAgendaPayload, ValidateAgendasBatch, GetExistingAgenda, GetCourseProgressSummary };